import React, { useMemo } from 'react';
import { Box, Card, CardContent, Typography, Grid } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import {
  OverviewDashboard,
  FormTypeSummary,
  StatusCount,
} from '../../../types/annualReports';
import { useNavigate } from 'react-router-dom';
// Import status definitions from variables
import {
  local_chapter_roster_dues_statuses,
  spd_roster_dues_statuses,
  irs_lc_statuses,
  irs_spd_statuses,
  irs_reciprocity_statuses,
} from '../../../utils/variables';

interface OverviewViewProps {
  data: OverviewDashboard;
}

// Helper to merge backend counts with client-side status definitions
const mergeStatusCounts = (
  backendCounts: Array<{ status_id: string; count: number }>,
  statusDefinitions: Array<{
    id: string;
    name: string;
    order: number;
    color: string;
  }>
): Array<StatusCount & { color: string }> => {
  // Create a map of status_id -> count from backend
  const countMap = new Map<string, number>();
  backendCounts.forEach((item) => {
    countMap.set(item.status_id, item.count);
  });

  // Merge with status definitions, ensuring all statuses are included
  return statusDefinitions.map((def) => ({
    status_id: def.id,
    status_name: def.name,
    count: countMap.get(def.id) || 0,
    display_order: def.order,
    color: def.color,
  }));
};

const OverviewView: React.FC<OverviewViewProps> = ({ data }) => {
  const navigate = useNavigate();

  // Determine if a status represents "completed"
  const isCompletedStatus = (statusName: string): boolean => {
    const completedKeywords = [
      'completed',
      'approved',
      'finished',
      'done',
      'submitted',
    ];
    return completedKeywords.some((keyword) =>
      statusName.toLowerCase().includes(keyword)
    );
  };

  // FormTile component to allow use of hooks
  const FormTile: React.FC<{
    title: string;
    summary: FormTypeSummary;
    color: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
    formType:
      | 'roster_dues_lc'
      | 'roster_dues_spd'
      | 'irs_lc'
      | 'irs_sc'
      | 'irs_rg';
  }> = ({ title, summary, color, formType }) => {
    // Get status definitions based on form type
    const statusDefinitions = useMemo(() => {
      switch (formType) {
        case 'roster_dues_lc':
          return local_chapter_roster_dues_statuses;
        case 'roster_dues_spd':
          return spd_roster_dues_statuses;
        case 'irs_lc':
          return irs_lc_statuses;
        case 'irs_sc':
          return irs_spd_statuses;
        case 'irs_rg':
          return irs_reciprocity_statuses;
        default:
          return [];
      }
    }, [formType]);

    // Merge backend counts with client-side definitions
    const mergedStatusBreakdown = useMemo(() => {
      // Backend now only returns status_id and count
      const backendCounts = summary.status_breakdown.map((s) => ({
        status_id: s.status_id,
        count: s.count,
      }));
      return mergeStatusCounts(backendCounts, statusDefinitions);
    }, [summary.status_breakdown, statusDefinitions]);

    // Transform merged status breakdown into pie chart data with colors
    const pieData = useMemo(() => {
      if (mergedStatusBreakdown.length === 0) {
        return [];
      }
      return mergedStatusBreakdown.map((status, index) => ({
        id: index,
        value: status.count > 0 ? status.count : 0.1, // Use 0.1 for zero counts so they show on chart
        label: status.status_name || '',
        color: status.color, // Use color directly from merged status
      }));
    }, [mergedStatusBreakdown]);

    // Calculate completion percentage
    const completionPercentage = useMemo(() => {
      if (summary.total_count === 0) return 0;
      const completedCount = mergedStatusBreakdown
        .filter(
          (status) =>
            status.status_name && isCompletedStatus(status.status_name)
        )
        .reduce((sum, status) => sum + status.count, 0);
      return Math.round((completedCount / summary.total_count) * 100);
    }, [summary, mergedStatusBreakdown]);

    return (
      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
        <Card
          sx={{
            height: '100%',
            cursor: 'pointer',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 4,
            },
          }}
          onClick={() => navigate(summary.link)}
        >
          <CardContent>
            <Typography variant="h6" gutterBottom align="center">
              {title}
            </Typography>

            {summary.total_count === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 200,
                }}
              >
                <Typography variant="h4" color="text.secondary" sx={{ mb: 1 }}>
                  0
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No forms found
                </Typography>
              </Box>
            ) : (
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mb: 2,
                    width: '100%',
                  }}
                >
                  <PieChart
                    series={[
                      {
                        data: pieData,
                        innerRadius: 70,
                        outerRadius: 110,
                        paddingAngle: 2,
                        cornerRadius: 5,
                        highlightScope: {
                          faded: 'global',
                          highlighted: 'item',
                        },
                        arcLabel: () => '', // Return empty string to hide labels
                      },
                    ]}
                    width={300}
                    height={280}
                    slotProps={{
                      legend: {
                        hidden: true,
                      },
                    }}
                    sx={{
                      '& .MuiChartsLegend-root': {
                        display: 'none',
                      },
                      '& .MuiChartsArcLabel-root': {
                        display: 'none', // Hide any arc labels
                      },
                    }}
                  />
                </Box>
                {/* Total count and completion info below chart */}
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <Typography
                    variant="h5"
                    color="primary.main"
                    fontWeight="bold"
                  >
                    {summary.total_count}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Forms
                  </Typography>
                  {completionPercentage > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography
                        variant="body1"
                        color={
                          completionPercentage === 100
                            ? 'success.main'
                            : 'text.primary'
                        }
                        fontWeight="medium"
                      >
                        {completionPercentage}% Complete
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* Status breakdown list - show all statuses even with count=0 */}
            {mergedStatusBreakdown.length > 0 && (
              <Box sx={{ mt: 2, maxHeight: 150, overflowY: 'auto' }}>
                {mergedStatusBreakdown.map((status) => (
                  <Box
                    key={status.status_id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 0.5,
                      fontSize: '0.75rem',
                    }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: status.color, // Use color directly from merged status
                        mr: 1,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {status.status_name || 'Unknown'}
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      sx={{ ml: 1 }}
                    >
                      {status.count}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    );
  };

  return (
    <Box>
      {data.spd_chapter_name && (
        <>
          <Typography variant="h5" gutterBottom>
            {data.spd_chapter_name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Annual Reports Overview
          </Typography>
        </>
      )}
      {!data.spd_chapter_name && (
        <>
          <Typography variant="h5" gutterBottom>
            Annual Reports Overview
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            All annual reporting forms
          </Typography>
        </>
      )}

      <Grid container spacing={2}>
        <FormTile
          title="Local Chapter Roster & Dues"
          summary={data.roster_dues_lc}
          color="primary"
          formType="roster_dues_lc"
        />
        <FormTile
          title="SPD Chapter Roster & Dues"
          summary={data.roster_dues_spd}
          color="secondary"
          formType="roster_dues_spd"
        />
        <FormTile
          title="IRS-LC"
          summary={data.irs_lc}
          color="success"
          formType="irs_lc"
        />
        <FormTile
          title="IRS-SC"
          summary={data.irs_sc}
          color="warning"
          formType="irs_sc"
        />
        <FormTile
          title="IRS-RG"
          summary={data.irs_rg}
          color="error"
          formType="irs_rg"
        />
      </Grid>
    </Box>
  );
};

export default OverviewView;
